// Module 5 — List Your Debt
// ./index.ts owns this module's key and display name; this file owns its
// action items.

// Libs
import { useParams } from 'react-router';

// RDS
import { VideoOutlinedIcon } from '@ramsey-design-system/icons';
import Text from '@ramsey-design-system/text';

// Components
import DiscussionQuestions, {
  DiscussionQuestion
} from '../DiscussionQuestions';
import HLSVideoPlayer from '~fpu/shared/components/HLSVideoPlayer';
import QrCodePrompt from '../QrCodePrompt';

// Utils
import { PATHS } from '~fpu/config/constants';
import storeConfig from '~fpu/config/storeConfig';

// Types
import { WorkshopActionItemDefinition } from '../../types';

function DebtFreeScreamClip() {
  return (
    <HLSVideoPlayer
      src="https://cdn.ramseysolutions.net/media/ramsey-plus/financial-peace/coordinator/dashboard/budget-workshop-assets/debt-free-scream.mp4"
      thumbnail="https://cdn.ramseysolutions.net/media/ramsey-plus/financial-peace/coordinator/dashboard/budget-workshop-assets/debt-free-scream.jpg"
    />
  );
}

function DebtSnowballMethodClip() {
  return (
    <HLSVideoPlayer
      src="https://cdn.ramseysolutions.net/media/ramsey-plus/financial-peace/coordinator/dashboard/budget-workshop-assets/list-your-debts.mp4"
      thumbnail="https://cdn.ramseysolutions.net/media/ramsey-plus/financial-peace/coordinator/dashboard/budget-workshop-assets/list-your-debts.jpg"
    />
  );
}

function TypesOfDebt() {
  return (
    <div className="flex flex-col gap-4">
      <Text className="text-4 leading-lg">
        Debt is any money you owe to someone else — anything you borrowed and
        have not paid back. Ramsey includes all outstanding balances, not just
        the minimum payments.
      </Text>
      <ul className="text-4 leading-lg ml-2 list-disc">
        <li>Credit cards</li>
        <li>Student loans</li>
        <li>Car loans</li>
        <li>Medical bills</li>
        <li>Personal loans</li>
        <li>Payday loans</li>
        <li>Money owed to family or friends</li>
        <li>Store or retail credit cards</li>
      </ul>
    </div>
  );
}

function ListYourDebtActivity() {
  const { workshopId } = useParams() as { workshopId: string };
  const activityUrl = `${storeConfig.appUrl}${PATHS.MEMBER_DASHBOARD.BUDGET_EXERCISE(workshopId)}`;

  return (
    <QrCodePrompt
      activityUrl={activityUrl}
      slug="list-your-debt-activity"
      dialogTitle="List Your Debt Activity QR Code"
      linkLabel="Budget Activity"
    >
      <Text className="text-5 text-blue-90 leading-lg font-bold">
        Scan the QR code to add your debts to your budget!
      </Text>
      <Text className="text-4 leading-lg">
        For this activity, focus on listing every debt you owe and its minimum
        payment. Any payments over the minimum should be applied to the debt
        with the smallest balance.
      </Text>
      <div className="border-gray-20 mt-1 overflow-hidden rounded-[20px] border bg-white p-5">
        <img
          src="https://cdn.ramseysolutions.net/media/ramsey-plus/financial-peace/coordinator/dashboard/debt-animation.gif"
          alt="Step 4 of 5, List Your Debt"
          className="h-auto w-full [clip-path:inset(0.4%_1.5%)]"
        />
      </div>
    </QrCodePrompt>
  );
}

const DISCUSSION_QUESTIONS: DiscussionQuestion[] = [
  {
    icon: 'https://cdn.ramseysolutions.net/media/ramsey-plus/financial-peace/coordinator/dashboard/budget-workshop-assets/discussion-icon-1.png',
    text: 'Have you ever listed all of your debts in one place before today?'
  },
  {
    icon: 'https://cdn.ramseysolutions.net/media/ramsey-plus/financial-peace/coordinator/dashboard/budget-workshop-assets/discussion-icon-2.png',
    text: 'When you wrote everything down, did the total surprise you?'
  },
  {
    icon: 'https://cdn.ramseysolutions.net/media/ramsey-plus/financial-peace/coordinator/dashboard/budget-workshop-assets/discussion-icon-3.png',
    text: 'What would it feel like to cross the first debt off that list?'
  }
];

function Discussion() {
  return <DiscussionQuestions questions={DISCUSSION_QUESTIONS} />;
}

const actionItems: WorkshopActionItemDefinition[] = [
  {
    title: 'Debt Free Scream',
    Content: DebtFreeScreamClip,
    icon: VideoOutlinedIcon,
    duration: '8:26'
  },
  {
    title: 'Debt Snowball Method',
    Content: DebtSnowballMethodClip,
    icon: VideoOutlinedIcon,
    duration: '7:06'
  },
  { title: 'Types of Debt', Content: TypesOfDebt },
  { title: 'List Your Debt Activity', Content: ListYourDebtActivity },
  { title: 'Discussion', Content: Discussion }
];

export default actionItems;
